import { z } from 'zod';

export const PROJECT_SCHEMA_VERSION = 1 as const;

const Vec2Schema = z.tuple([z.number(), z.number()]);
const Vec3Schema = z.tuple([z.number(), z.number(), z.number()]);
const AnimatableValueSchema = z.union([z.number(), z.string(), z.boolean(), Vec2Schema, Vec3Schema]);

export const KeyframeSchema = z.object({
  id: z.string(),
  time: z.number().nonnegative(),
  value: AnimatableValueSchema,
  interpolation: z.enum(['linear', 'hold', 'ease-in', 'ease-out', 'ease-in-out', 'overshoot', 'bounce', 'bezier']).default('linear'),
  bezier: z.tuple([z.number(), z.number(), z.number(), z.number()]).optional()
});

export const TransformSchema = z.object({
  position: Vec3Schema.default([0, 0, 0]),
  scale: Vec3Schema.default([1, 1, 1]),
  rotation: Vec3Schema.default([0, 0, 0]),
  anchor: Vec3Schema.default([0, 0, 0]),
  opacity: z.number().min(0).max(1).default(1)
});

export const EffectSchema = z.object({
  id: z.string(),
  type: z.enum(['brightness', 'contrast', 'saturation', 'hue', 'blur', 'sharpen', 'vignette', 'noise', 'chromatic-aberration']),
  name: z.string(),
  enabled: z.boolean().default(true),
  params: z.record(z.union([z.number(), z.string(), z.boolean()])),
  keyframes: z.record(z.array(KeyframeSchema)).default({})
});

export const MaskSchema = z.object({
  id: z.string(),
  type: z.enum(['rectangle', 'ellipse', 'path']),
  mode: z.enum(['add', 'subtract', 'intersect']).default('add'),
  inverted: z.boolean().default(false),
  feather: z.number().nonnegative().default(0),
  points: z.array(Vec2Schema).default([])
});

export const AssetSchema = z.object({
  id: z.string(),
  name: z.string(),
  kind: z.enum(['video', 'audio', 'image', 'model', 'font', 'other']),
  mimeType: z.string(),
  size: z.number().nonnegative(),
  duration: z.number().nonnegative().optional(),
  width: z.number().int().positive().optional(),
  height: z.number().int().positive().optional(),
  fps: z.number().positive().optional(),
  channels: z.number().int().positive().optional(),
  sampleRate: z.number().positive().optional(),
  hash: z.string().optional(),
  missing: z.boolean().default(false),
  proxyStatus: z.enum(['none', 'queued', 'ready', 'error']).default('none'),
  cacheStatus: z.enum(['local', 'linked', 'missing']).default('local'),
  createdAt: z.number()
});

const MaterialSchema = z.object({
  color: z.string().default('#9ca7ff'),
  metallic: z.number().min(0).max(1).default(0.15),
  roughness: z.number().min(0).max(1).default(0.55),
  emission: z.string().default('#000000'),
  opacity: z.number().min(0).max(1).default(1)
});

const SceneObjectSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.enum(['cube', 'sphere', 'plane', 'text', 'group', 'light', 'camera']),
  parentId: z.string().nullable().default(null),
  visible: z.boolean().default(true),
  locked: z.boolean().default(false),
  transform: TransformSchema,
  material: MaterialSchema.optional()
});

export const LayerSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.enum(['video', 'audio', 'image', 'text', 'shape', 'caption', 'scene3d', 'adjustment', 'null', 'group']),
  assetId: z.string().optional(),
  nestedCompositionId: z.string().optional(),
  parentId: z.string().nullable().default(null),
  compositionId: z.string(),
  visible: z.boolean().default(true),
  locked: z.boolean().default(false),
  muted: z.boolean().default(false),
  solo: z.boolean().default(false),
  blendMode: z.enum(['normal', 'multiply', 'screen', 'overlay', 'soft-light', 'darken', 'lighten', 'difference']).default('normal'),
  start: z.number().nonnegative(),
  duration: z.number().positive(),
  inPoint: z.number().nonnegative().default(0),
  speed: z.number().min(0.05).max(16).default(1),
  reverse: z.boolean().default(false),
  transform: TransformSchema,
  keyframes: z.record(z.array(KeyframeSchema)).default({}),
  effects: z.array(EffectSchema).default([]),
  masks: z.array(MaskSchema).default([]),
  color: z.string().optional(),
  text: z.string().optional(),
  fontSize: z.number().positive().optional(),
  fontFamily: z.string().optional(),
  fontWeight: z.number().optional(),
  textAlign: z.enum(['left', 'center', 'right']).optional(),
  volume: z.number().min(0).max(2).default(1),
  fadeIn: z.number().nonnegative().default(0),
  fadeOut: z.number().nonnegative().default(0),
  scene: z.object({
    objects: z.array(SceneObjectSchema),
    activeObjectId: z.string().nullable(),
    background: z.string().default('#20242c'),
    cameraPosition: Vec3Schema.default([4, 3, 6]),
    cameraTarget: Vec3Schema.default([0, 0, 0])
  }).optional()
});

export const MarkerSchema = z.object({
  id: z.string(),
  time: z.number().nonnegative(),
  label: z.string(),
  color: z.string().default('#e6ff55')
});

export const CompositionSchema = z.object({
  id: z.string(),
  name: z.string(),
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  fps: z.number().positive(),
  duration: z.number().positive(),
  backgroundColor: z.string(),
  layerIds: z.array(z.string()),
  markers: z.array(MarkerSchema).default([]),
  parentCompositionId: z.string().nullable().default(null)
});

export const ProjectSchema = z.object({
  schemaVersion: z.literal(PROJECT_SCHEMA_VERSION),
  id: z.string(),
  name: z.string().min(1),
  createdAt: z.number(),
  updatedAt: z.number(),
  activeCompositionId: z.string(),
  compositions: z.record(CompositionSchema),
  layers: z.record(LayerSchema),
  assets: z.record(AssetSchema),
  metadata: z.object({
    author: z.string().default(''),
    description: z.string().default(''),
    appVersion: z.string().default('0.1.0')
  })
});

export type Project = z.infer<typeof ProjectSchema>;
export type Composition = z.infer<typeof CompositionSchema>;
export type Layer = z.infer<typeof LayerSchema>;
export type Asset = z.infer<typeof AssetSchema>;
export type Effect = z.infer<typeof EffectSchema>;
export type Keyframe = z.infer<typeof KeyframeSchema>;
export type Marker = z.infer<typeof MarkerSchema>;
export type Transform = z.infer<typeof TransformSchema>;
export type SceneObject = NonNullable<Layer['scene']>['objects'][number];
export type AnimatableValue = z.infer<typeof AnimatableValueSchema>;

export function parseProject(input: unknown): Project {
  return ProjectSchema.parse(input);
}
